import * as auth0 from 'auth0-js';
import * as moment from 'moment';
import history from '../../history';

import { store } from '../../store';
import { fetchSavedDocumentsRequest } from '../Saved/actions';
import { clearUserProfile, setUserProfile } from '../User/actions';
import { logError } from '../../utils/logger';
export { logError };

const requireEnv = (name: string, value: string | undefined): string => {
  if (!value) {
    throw new Error(`Missing required environment variable: ${name}`);
  }
  return value;
};

const audience = requireEnv('REACT_APP_AUTH0_AUDIENCE', process.env.REACT_APP_AUTH0_AUDIENCE);
const clientId = requireEnv('REACT_APP_AUTH0_CLIENT_ID', process.env.REACT_APP_AUTH0_CLIENT_ID);
const redirectUri = requireEnv('REACT_APP_AUTH0_REDIRECT_URI', process.env.REACT_APP_AUTH0_REDIRECT_URI);
const domain = requireEnv('REACT_APP_AUTH0_DOMAIN', process.env.REACT_APP_AUTH0_DOMAIN);
const namespace = requireEnv('REACT_APP_AUTH0_NAMESPACE', process.env.REACT_APP_AUTH0_NAMESPACE);
const trialLengthInDays: number = Number(process.env.REACT_APP_TRIAL_PERIOD_TIME) || 14;
const trialEndingWarningLengthInDays: number = Number(process.env.REACT_APP_TRIAL_PERIOD_ENDING_TIME) || 3;

const auth = new auth0.WebAuth({
  audience,
  clientID: clientId,
  domain,
  redirectUri,
  responseType: 'token id_token',
  scope: 'openid permissions profile groups roles email',
});

export const login = (loginHint: string = '') => {
  auth.authorize({ login_hint: loginHint });
};

export interface SpinbaseUserProfile extends auth0.Auth0UserProfile {
  [key: string]: any;
}

export let userProfile: SpinbaseUserProfile;
let tokenRenewalTimeout: any;

export const getProfile = (cb?: any): void => {
  const accessToken = getAccessToken();
  if (accessToken) {
    auth.client.userInfo(accessToken, (err: any, profile: any) => {
      if (profile) {
        userProfile = profile;
        store.dispatch(setUserProfile(profile));
        store.dispatch(fetchSavedDocumentsRequest());
        localStorage.setItem('isEmailVerified', JSON.stringify(isEmailVerified()));
        localStorage.setItem('isTrialUser', JSON.stringify(isTrialUser()));
        localStorage.setItem('userEmail', (userProfile.email || '').toLowerCase());
      }
      if (cb) {
        cb(err, profile);
      }
    });
  }
};

export const handleAuthentication = () => {
  auth.parseHash(async (err, authResult) => {
    if (authResult && authResult.accessToken && authResult.idToken) {
      setSession(authResult);
      getProfile(() => {
        if (isFirstLogin()) {
          history.push('/license-agreement');
          return;
        }
      });
      history.push('/search');
    } else if (err) {
      history.push('/search');
      logError(err);
    }
  });
};

export const getAccessToken = () => {
  const accessToken = localStorage.getItem('access_token');
  if (!accessToken) {
    return '';
  }
  return accessToken;
};

export const setSession = (authResult: any) => {
  const scopes = authResult.scope || '';
  const expiresAt = JSON.stringify(authResult.expiresIn * 1000 + moment().valueOf());
  localStorage.setItem('access_token', authResult.accessToken);
  localStorage.setItem('id_token', authResult.idToken);
  localStorage.setItem('expires_at', expiresAt);
  localStorage.setItem('scopes', JSON.stringify(scopes));
  localStorage.setItem('nickname', authResult.idTokenPayload.nickname);

  scheduleRenewal();
};

export const logout = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('id_token');
  localStorage.removeItem('expires_at');
  localStorage.removeItem('scopes');
  localStorage.removeItem('isEmailVerified');
  localStorage.removeItem('isTrialUser');
  localStorage.removeItem('userEmail');
  localStorage.removeItem('nickname');

  userProfile = {
    clientID: '',
    created_at: '',
    identities: [],
    name: '',
    nickname: '',
    picture: '',
    sub: '',
    updated_at: '',
    user_id: '',
    email_verified: false,
  };

  auth.logout({});

  store.dispatch(clearUserProfile());
  clearTimeout(tokenRenewalTimeout);
  history.push('/');
};

export const renewToken = () => {
  auth.checkSession({}, (err, result) => {
    if (err) {
      logError(err);
    } else {
      setSession(result);
    }
  });
};

export const scheduleRenewal = () => {
  const expiresAt = JSON.parse(localStorage.getItem('expires_at') as string);
  const delay = expiresAt - Date.now();
  if (delay > 0) {
    tokenRenewalTimeout = setTimeout(() => {
      renewToken();
    }, delay);
  }
};
export const isAuthenticated = () => {
  const expiresAt = JSON.parse(localStorage.getItem('expires_at') || '0');
  return moment().valueOf() < expiresAt;
};

const getClaimUrl = () => {
  return `${namespace}/claims/`;
};

export const isAuthorizationBasedOnUserList = () => {
  return hasRole('LIST_BASED_AUTHORIZED') || hasRole('LIST_BASED_UNAUTHORIZED');
};

export const isUserAuthorizedBasedOnList = () => {
  return hasRole('LIST_BASED_AUTHORIZED');
};

export const isUserUnAuthorizedBasedOnList = () => {
  return hasRole('LIST_BASED_UNAUTHORIZED');
};

export const canUseSearch = () => {
  const isTrialUserNotVerified: boolean = isTrialUser() && !isEmailVerified();
  const trialBasedPermission = !hasTrialExpired() && !isTrialUserNotVerified;
  const listBasedAuthorized = isUserAuthorizedBasedOnList() && !isUserUnAuthorizedBasedOnList();
  return (
    hasPermission('use:search') &&
    (isAuthorizationBasedOnUserList() ? listBasedAuthorized || !isTrialUser() : trialBasedPermission)
  );
};

export const hasPermission = (permission: string) => {
  if (!userProfile) {
    return false;
  }
  const permissions = userProfile[`${getClaimUrl()}permissions`];
  return Array.isArray(permissions) && permissions.some((val: string) => val === permission);
};

export const hasRole = (role: string) => {
  if (!userProfile) {
    return false;
  }
  const roles = userProfile[`${getClaimUrl()}roles`];
  return Array.isArray(roles) && roles.some((val: string) => val === role);
};

export const trialWillExpire = () => {
  return isTrialUser() && getTrialDaysLeft() < trialEndingWarningLengthInDays + 1;
};

export const hasTrialExpired = () => {
  const isTrial: boolean = isTrialUser();
  const trialTimePassed: boolean = getTrialDaysLeft() < 1;
  return isTrial && trialTimePassed;
};

export const getTrialDaysLeft = (): number => {
  if (!userProfile) {
    return 0;
  }
  const currentDate = moment();
  const userCreatedAt = moment(userProfile[`${getClaimUrl()}created_at`]);
  return trialLengthInDays - currentDate.diff(userCreatedAt, 'days');
};

export const isTrialUser = (): boolean => {
  const hasTrialRole: boolean = hasRole('Trial User');
  const hasPaidRole: boolean = hasRole('User') || hasRole('Admin');
  return hasTrialRole && !hasPaidRole;
};

export const isWaadUser = (): boolean => {
  // userProfile comes from the standard OIDC /userinfo endpoint, which does
  // not return the `identities` array (that's Management-API-only) - `sub`
  // is the one field guaranteed present, and waad connections always build
  // it as `waad|<identifier>`.
  const identifier = userProfile?.sub || userProfile?.user_id || '';
  return identifier.startsWith('waad|');
};

export const isEmailVerified = (): boolean => {
  // Microsoft SSO forces email_verified to false (required for the Entra ID
  // common endpoint to accept external tenants), so the claim is never true
  // for waad users regardless of their actual account status.
  return isWaadUser() || userProfile.email_verified || false;
};

export const getLoginCount = (): number => {
  return userProfile ? (userProfile[`${getClaimUrl()}logins`] as number) : 0;
};

export const isFirstLogin = (): boolean => {
  return getLoginCount() === 1;
};
